import React from 'react';
import { Sparkles, MapPin, Calendar, Clock, Users, Music, Wine, Utensils, Palette } from 'lucide-react';

const WhatToExpectSection = () => {
  return (
    <section
      id="what-to-expect"
      className="py-20 sm:py-24 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden"
      data-testid="what-to-expect-section"
    >
      {/* Decorative gradient orbs */}
      <div className="absolute top-0 right-1/4 w-[28rem] h-[28rem] bg-orange-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/4 w-[28rem] h-[28rem] bg-pink-500/10 rounded-full blur-3xl" />

      <div className="max-w-5xl mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/30 mb-4">
            <Sparkles className="w-4 h-4 text-orange-400" />
            <span className="text-orange-300 text-sm font-medium tracking-wider uppercase">What to Expect</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 mb-6">
            Hawaii&apos;s Premier Tequila Festival
          </h2>
        </div>

        {/* Natural-language paragraph blocks for AEO */}
        <div className="prose prose-invert max-w-none space-y-6 text-white/80 leading-relaxed text-base sm:text-lg mb-12">
          <p>
            The <strong className="text-white">Aloha Tequila & Arts Festival</strong> is Honolulu&apos;s first
            outdoor festival dedicated entirely to tequila, mezcal, and agave spirits — paired with Hawaiian
            art, curated street food, live music, and a flea market. It&apos;s the headline event for anyone
            looking for <em>things to do in Oahu the weekend of June 18–21, 2026</em>, and one of the top
            tequila festivals in the United States in 2026.
          </p>
          <p>
            Held at the iconic <strong className="text-white">International Market Place in Waikiki</strong>{' '}
            on Saturday, June 20th from 5:00 PM to 9:00 PM, the festival brings together 80+ premium tequila
            and mezcal brands, Oahu&apos;s top chefs and taco vendors, live painting, mariachi and DJs,
            and an artisan mercado. It&apos;s a 21+ event, and partial proceeds benefit the{' '}
            <strong className="text-white">Aloha Cancer Project</strong>, a Honolulu-based 501(c)(3)
            supporting cancer patients and their families across Hawaii.
          </p>
          <p>
            Whether you&apos;re visiting Oahu for the weekend or you&apos;re a local searching for the best food and
            drink festivals in Hawaii, the Aloha Tequila & Arts Festival offers a one-of-a-kind taste of
            agave culture in a tropical Waikiki setting. Buy tickets via TicketSpice, follow{' '}
            <a
              href="https://www.instagram.com/alohatequilafest"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-400 hover:text-orange-400 transition-colors"
              data-testid="wte-instagram-link"
            >
              @alohatequilafest
            </a>{' '}
            on Instagram for lineup updates, and tag your photos with{' '}
            <strong className="text-pink-300">#AlohaTequilaFest</strong>.
          </p>
        </div>

        {/* Quick-fact grid (AI-friendly structured info) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {[
            { icon: Calendar, label: 'Date', value: 'Saturday, June 20, 2026' },
            { icon: Clock, label: 'Time', value: '5:00 PM – 9:00 PM' },
            { icon: MapPin, label: 'Venue', value: 'International Market Place, Waikiki' },
            { icon: Users, label: 'Age', value: '21+ with valid ID' },
            { icon: Wine, label: 'Spirits', value: '80+ tequilas & agave brands' },
            { icon: Utensils, label: 'Food', value: 'Top Oahu restaurants & taco stations' },
            { icon: Music, label: 'Entertainment', value: 'Live music, DJs & mariachi' },
            { icon: Palette, label: 'Art', value: 'Live painting, flea market & mercado' },
            { icon: Sparkles, label: 'Cause', value: 'Aloha Cancer Project beneficiary' }
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 hover:border-pink-500/40 hover:bg-white/[0.07] transition-all duration-300"
                data-testid={`wte-fact-${i}`}
              >
                <Icon className="w-5 h-5 text-pink-400 mb-2" />
                <p className="text-white/50 text-xs uppercase tracking-wider mb-1">{item.label}</p>
                <p className="text-white text-sm font-semibold leading-snug">{item.value}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatToExpectSection;
